<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <card>
      <!-- Header: active/archived tabs + create -->
      <div class="tabs-header d-flex align-items-end mb-3">
        <tab-rail
          :tabs="[{ name: 'Active' }, { name: 'Archived' }]"
          :value="activeTab"
          class="flex-grow-1"
          @input="switchTab"
        ></tab-rail>
        <base-button type="primary" size="sm" class="ml-3 tabs-header__action d-none d-sm-block" @click="createCampaign">
          <i class="tim-icons mr-1 el-icon-plus"></i> Create campaign
        </base-button>
        <base-button type="primary" size="sm" class="ml-3 tabs-header__action d-block d-sm-none" @click="createCampaign">
          <i class="tim-icons el-icon-plus"></i>
        </base-button>
      </div>

      <!-- Filters -->
      <filter-bar
        :filters="filterSchema"
        :initial-value="filterValues"
        :result-count="totalRows"
        :loading="refreshing"
        :per-page="perPage"
        saved-filters-key="campaigns"
        @change="onFiltersChanged"
        @update:perPage="onPerPageChange"
      />

      <!-- Empty state -->
      <div v-if="campaigns.length === 0 && !loading" class="cl-empty-state mt-4">
        <img src="/img/pandas/sms.png" alt="No campaigns" class="empty-state-img" />
        <h4 class="empty-state-text" v-if="view === 'active'">{{ hasActiveFilters ? 'No campaigns match your filters' : 'No campaigns created yet' }}</h4>
        <h4 class="empty-state-text" v-else>No archived campaigns</h4>
        <p class="cl-empty-state__hint" v-if="view === 'active' && !hasActiveFilters">
          Campaigns send one of your email templates to a targeted group of customers, with delivery and engagement tracking
        </p>
      </div>

      <!-- Card list -->
      <div v-if="campaigns.length > 0" class="cl-list mt-3">
        <div
          v-for="campaign in campaigns"
          :key="'C' + campaign.id"
          class="cl-card"
          :class="{ 'cl-card--archived': campaign.archived === 1 }"
          @click="openCampaign(campaign)"
        >
          <div class="cl-card__body">
            <!-- Center: name + metadata -->
            <div class="cl-card__info">
              <div class="cl-card__label text-white">{{ campaign.name || 'Untitled campaign' }}</div>

              <div class="cl-card__meta">
                <span class="cl-card__status" :class="'text-' + statusColor(campaign.status)">
                  <i v-if="campaign.status === 'sending'" class="el-icon-loading mr-1"></i>{{ statusLabel(campaign) }}
                </span>
                <span v-if="campaign.recurrence === 'daily'" class="cl-card__meta-detail">
                  runs daily at {{ sendTimeDisplay(campaign) }}<template v-if="campaign.ends_on"> until {{ formatGroupDate(campaign.ends_on, { dayOfWeek: false }) }}</template>
                </span>
                <span v-if="campaign.description" class="cl-card__meta-detail">{{ campaign.description }}</span>
              </div>

              <div class="cl-card__usage">
                <template v-if="campaign.recurrence === 'daily'">
                  <template v-if="campaign.last_run_date">
                    <span class="cl-card__usage-label">Last run</span>
                    <span class="cl-card__usage-text">{{ formatGroupDate(campaign.last_run_date, { dayOfWeek: false }) }}</span>
                  </template>
                  <template v-else-if="campaign.status === 'scheduled' && campaign.scheduled_at">
                    <span class="cl-card__usage-label">First run</span>
                    <span class="cl-card__usage-text">{{ formatDateTime(campaign.scheduled_at) }}</span>
                  </template>
                  <template v-else>
                    <span class="cl-card__usage-label">Created</span>
                    <span class="cl-card__usage-text">{{ formatDateTime(campaign.created_at) }}</span>
                  </template>
                </template>
                <template v-else-if="campaign.recipient_count !== null">
                  <span class="cl-card__usage-label">Recipients</span>
                  <span class="cl-card__usage-text">{{ campaign.recipient_count }}<template v-if="campaign.sent_count !== null">, {{ campaign.sent_count }} sent, {{ campaign.failed_count || 0 }} failed</template></span>
                </template>
                <template v-if="campaign.sent_at">
                  <span class="cl-card__usage-label">Sent</span>
                  <span class="cl-card__usage-text">{{ formatDateTime(campaign.sent_at) }}</span>
                </template>
                <template v-else-if="campaign.scheduled_at">
                  <span class="cl-card__usage-label">Scheduled for</span>
                  <span class="cl-card__usage-text">{{ formatDateTime(campaign.scheduled_at) }}</span>
                </template>
                <template v-else>
                  <span class="cl-card__usage-label">Created</span>
                  <span class="cl-card__usage-text">{{ formatDateTime(campaign.created_at) }}</span>
                </template>
                <badge type="default" class="cl-card__utm-token" title="Copy to clipboard" @click.native.stop="copyUtmToken(campaign)">
                  campaign-{{ campaign.id }} <i class="tim-icons icon-single-copy-04"></i>
                </badge>
              </div>
            </div>

            <!-- Right: badges + actions (click-stopped so the row click cannot swallow the menu) -->
            <div class="cl-card__actions" @click.stop>
              <span v-if="campaign.archived === 1" class="badge-tag badge-tag--archived">Archived</span>

              <el-dropdown @click.native.stop>
                <base-button simple type="primary" link size="sm">
                  <i class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a v-if="isEditable(campaign)" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editCampaign(campaign)">
                      <i class="tim-icons icon-pencil mr-2"></i> Edit campaign
                    </a>
                    <a v-if="['scheduled','sending'].includes(campaign.status)" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="cancelPrompt(campaign)">
                      <i class="tim-icons icon-button-pause mr-2"></i> {{ campaign.recurrence === 'daily' ? 'Stop campaign' : 'Cancel campaign' }}
                    </a>
                    <a v-if="['sending','sent','cancelled','failed'].includes(campaign.status) || campaign.recurrence === 'daily'" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="viewReport(campaign)">
                      <i class="tim-icons icon-chart-bar-32 mr-2"></i> View report
                    </a>
                    <a v-if="['sent','cancelled','failed'].includes(campaign.status)" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="duplicateCampaign(campaign)">
                      <i class="tim-icons icon-single-copy-04 mr-2"></i> Duplicate
                    </a>
                    <a v-if="['sent','cancelled','failed'].includes(campaign.status) && !campaign.archived" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="setArchived(campaign, true)">
                      <i class="tim-icons icon-app mr-2"></i> Archive
                    </a>
                    <a v-if="campaign.archived" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="setArchived(campaign, false)">
                      <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                    </a>
                    <template v-if="campaign.status === 'draft'">
                      <hr>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="deletePrompt(campaign)">
                        <i class="tim-icons icon-trash-simple mr-2"></i> Delete campaign
                      </a>
                    </template>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="row" v-if="totalRows > perPage">
        <div class="col d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="getCampaigns(false)"
          ></b-pagination>
        </div>
      </div>
    </card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import swal from 'sweetalert2'
import { BPagination } from 'bootstrap-vue'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import moment from 'moment-timezone'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { Badge } from 'src/components'
import TabRail from '@/components/TabRail.vue'

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const refreshing = ref(false)
const campaigns = ref<any[]>([])
const totalRows = ref(0)
const currentPage = ref(1)
const perPage = ref(10)
const view = ref('active')
const activeTab = ref(0)
const filterValues = ref<Record<string, any>>({ name: '', status: [] })

const filterSchema = [
  { key: 'name', type: 'text', label: 'Name', placeholder: 'Campaign name', primary: true },
  {
    key: 'status',
    type: 'multiselect',
    label: 'Status',
    primary: true,
    options: [
      { label: 'Draft', value: 'draft' },
      { label: 'Scheduled', value: 'scheduled' },
      { label: 'Sending', value: 'sending' },
      { label: 'Sent', value: 'sent' },
      { label: 'Cancelled', value: 'cancelled' },
      { label: 'Failed', value: 'failed' }
    ]
  }
]

const hasActiveFilters = computed(() =>
  !!(filterValues.value.name || (filterValues.value.status && filterValues.value.status.length)))

const STATUS_COLORS: Record<string, string> = {
  draft: 'muted',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
  cancelled: 'muted',
  failed: 'danger'
}
const statusColor = (status: string) => STATUS_COLORS[status] || 'muted'

// Ongoing (daily) campaigns reuse the status enum: scheduled = active,
// sent = finished (past the stop date), cancelled = stopped
function statusLabel(campaign: any): string {
  if (campaign.recurrence !== 'daily') return campaign.status
  if (campaign.status === 'scheduled') return 'active'
  if (campaign.status === 'sent') return 'finished'
  if (campaign.status === 'cancelled') return 'stopped'
  return campaign.status
}

function sendTimeDisplay(campaign: any): string {
  const hm = String(campaign.send_time || '09:00').slice(0, 5)
  const group = JSON.parse(localStorage.getItem('group') || '{}')
  const tz = group.tz_name || 'UTC'
  const m = moment.tz(`2000-01-01 ${hm}`, 'YYYY-MM-DD HH:mm', tz)
  return m.isValid() ? formatGroupTime(m.toISOString()) : hm
}

function formatDateTime(value: string) {
  const date = formatGroupDate(value, { dayOfWeek: false })
  const time = formatGroupTime(value)
  return `${date}, ${time}`
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

// While any listed campaign is sending, refresh quietly every 10s so the
// status flips to sent without a manual page reload (mirrors the report's
// poll cadence). The interval clears itself once nothing is sending.
let sendingPollTimer: ReturnType<typeof setInterval> | null = null
function syncSendingPoll() {
  const anySending = campaigns.value.some((c: any) => c.status === 'sending')
  if (anySending && !sendingPollTimer) {
    sendingPollTimer = setInterval(() => { getCampaigns(false) }, 10000)
  } else if (!anySending && sendingPollTimer) {
    clearInterval(sendingPollTimer)
    sendingPollTimer = null
  }
}
onBeforeUnmount(() => {
  if (sendingPollTimer) clearInterval(sendingPollTimer)
})

async function getCampaigns(showLoader = true) {
  if (showLoader) loading.value = true
  else refreshing.value = true
  try {
    const config = await authConfig()
    const offset = (currentPage.value - 1) * perPage.value
    let url = `${baseUrl}/email-campaigns?count=${perPage.value}&offset=${offset}`
    if (view.value === 'archived') url += '&archived=1'
    if (filterValues.value.name) url += `&name=${encodeURIComponent(filterValues.value.name)}`
    if (filterValues.value.status && filterValues.value.status.length) {
      url += `&status=${filterValues.value.status.join(',')}`
    }
    const response = await axios.get(url, config)
    campaigns.value = response.data.campaigns || []
    totalRows.value = response.data.total_rows || 0
    syncSendingPoll()
  } catch (err: any) {
    if (err?.response?.status === 401) {
      proxy.$router.push('/forbidden')
      return
    }
    swal('Error!', 'Failed to load campaigns', 'error')
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

function switchTab(index: number) {
  activeTab.value = index
  view.value = index === 1 ? 'archived' : 'active'
  currentPage.value = 1
  getCampaigns()
}

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = values
  currentPage.value = 1
  getCampaigns(false)
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  getCampaigns(false)
}

// Row click mirrors the POS list: editable campaigns open the editor,
// finished ones open their report
// An ongoing campaign that has started (active) is read only: its report
// shows the runs so far. Only a draft ongoing campaign opens in the editor.
function isEditable(campaign: any): boolean {
  if (campaign.recurrence === 'daily') return campaign.status === 'draft'
  return ['draft', 'scheduled'].includes(campaign.status)
}

function openCampaign(campaign: any) {
  if (isEditable(campaign)) {
    editCampaign(campaign)
  } else {
    viewReport(campaign)
  }
}

function createCampaign() {
  proxy.$router.push('/marketing/campaigns/new')
}

function editCampaign(campaign: any) {
  proxy.$router.push(`/marketing/campaigns/${campaign.id}`)
}

function viewReport(campaign: any) {
  proxy.$router.push(`/marketing/campaigns/${campaign.id}/report`)
}

function copyUtmToken(campaign: any) {
  navigator.clipboard.writeText(`campaign-${campaign.id}`)
  proxy.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
}

function duplicateCampaign(campaign: any) {
  proxy.$router.push(`/marketing/campaigns/new?from=${campaign.id}`)
}

function cancelPrompt(campaign: any) {
  const ongoing = campaign.recurrence === 'daily'
  swal({
    title: ongoing ? 'Stop this ongoing campaign?' : 'Cancel this campaign?',
    text: ongoing
      ? 'No more daily sends will go out. Runs already sent keep their reports.'
      : campaign.status === 'sending'
        ? 'Emails already sent cannot be recalled. Remaining recipients will be skipped.'
        : 'The campaign will not send at its scheduled time.',
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Cancel campaign',
    cancelButtonText: 'Keep it'
  }).then(async (result: any) => {
    if (!result.value) return
    try {
      const config = await authConfig()
      await axios.post(`${baseUrl}/email-campaigns/${campaign.id}/cancel`, {}, config)
      proxy.$notify({ type: 'success', message: 'Campaign cancelled', icon: 'tim-icons icon-check-2' })
    } catch (err) {
      swal('Error!', 'The campaign could not be cancelled', 'error')
    } finally {
      getCampaigns(false)
    }
  })
}

async function setArchived(campaign: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/email-campaigns/${campaign.id}/archive`, { archived }, config)
    proxy.$notify({
      type: 'success',
      message: archived ? 'Campaign archived' : 'Campaign restored',
      icon: 'tim-icons icon-check-2'
    })
  } catch (err) {
    swal('Error!', archived ? 'The campaign could not be archived' : 'The campaign could not be restored', 'error')
  } finally {
    getCampaigns(false)
  }
}

function deletePrompt(campaign: any) {
  swal({
    title: 'Delete this draft?',
    text: `"${campaign.name}" will be permanently removed.`,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Keep it'
  }).then(async (result: any) => {
    if (!result.value) return
    try {
      const config = await authConfig()
      await axios.delete(`${baseUrl}/email-campaigns/${campaign.id}`, config)
      proxy.$notify({ type: 'success', message: 'Draft deleted', icon: 'tim-icons icon-check-2' })
    } catch (err) {
      swal('Error!', 'The draft could not be deleted', 'error')
    } finally {
      getCampaigns(false)
    }
  })
}

// FilterBar (saved-views enabled) resolves precedence and drives the first fetch
// via its initial `change` (onFiltersChanged) — no self-fetch here.
onMounted(() => {})
</script>

<script lang="ts">
// el-dropdown is not globally registered (see globalComponents.js); register
// locally like every other consumer. Unaliased imports only.
import { Dropdown, DropdownMenu } from 'element-ui'

export default {
  name: 'campaigns-list',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu
  }
}
</script>

<style scoped>
/* Row-card list, same language as PaymentPagesList (pp-card) and the custom
   fields library. Layout + theme-neutral tints only. */
/* Tabs header mirrors CustomFieldsLibrary: the hairline moves to the flex
   container so it spans past the action button; TabRail's own border goes
   transparent to avoid doubling. */
.tabs-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.tabs-header ::v-deep .tab-rail {
  border-bottom-color: transparent;
  min-width: 0;
}
.tabs-header__action {
  margin-top: 0;
  margin-bottom: 14px;
}
.cl-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
}
.cl-empty-state__hint {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  max-width: 480px;
  margin: 8px auto 0;
}

.cl-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cl-card {
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.cl-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.cl-card--archived {
  opacity: 0.5;
  border-left-color: rgba(125, 125, 125, 0.4);
}
.cl-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
}


.cl-card__info {
  flex: 1;
  min-width: 0;
}
.cl-card__label {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cl-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}
.cl-card__status {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
}
.cl-card__meta-detail {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cl-card__usage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
}
.cl-card__usage-label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}
.cl-card__usage-label:not(:first-child) {
  margin-left: 8px;
}
.cl-card__usage-text {
  color: rgba(255, 255, 255, 0.75);
}
.cl-card__utm-token {
  cursor: pointer;
  text-transform: none;
  margin-bottom: 0;
}
.cl-card__utm-token i {
  font-size: 10px;
  margin-left: 2px;
}

.cl-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 575px) {
  .cl-card__body {
    gap: 10px;
  }
  .cl-card__usage-label {
    display: none;
  }
}
</style>

<style>
.white-content .tabs-header {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}
.white-content .cl-empty-state__hint {
  color: rgba(0, 0, 0, 0.6);
}
.white-content .cl-card {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .cl-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .cl-card__meta {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .cl-card__usage {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .cl-card__usage-label {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .cl-card__usage-text {
  color: rgba(0, 0, 0, 0.7);
}
</style>
